import React, { useState, useEffect } from "react";
import MainModal from "components/ModalMain";
import Button from "components/Button";
import Input from "components/Input";
import Checkbox from "components/Checkbox";
import PhoneNumberInput from "components/PhoneNumberInput";
import ModalGeneralSuccessfully from "screens/ModalGeneralSuccessfully";
import ModalGeneralFailed from "screens/ModalGeneralFailed";
import { usePlacesWidget } from "react-google-autocomplete";
import { useMutation } from "@apollo/client";
import { CREATE_STAFF } from "graphql/service";
import colors from "constants/colors";
import { Container, Footer, Head, Row } from "./styles";
import { debouncedErrorMessage } from "utils/messageUtil";

const ModalStaffInformation = ({
  isOpen,
  setIsOpen,
  roleId,
  role,
  setStaffs
}) => {

  const [successfullyIsOpen, setSuccessfullyIsOpen] = useState(false);
  const [failedIsOpen, setFailedIsOpen] = useState(false);

  const [staffInfo, setStaffInfo] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    streetAddress: "",
    streetAddressLine2: "",
    city: "",
    province: "",
    postalCode: "",
    country: "",
  });

  const [email, setEmail] = useState("");
  const [isDeactivated, setIsDeactivated] = useState(false);

  const [staffInfoError, setStaffInfoError] = useState({});

  const [phoneNumber, setPhoneNumber] = useState(staffInfo.phoneNumber);
  const [phoneNumberValidity, setPhoneNumberValidity] = useState(false);
  
  useEffect(() => {
    if (phoneNumberValidity) {
      if (phoneNumber !== staffInfo.phoneNumber) {
        setStaffInfoError({ ...staffInfoError, phoneNumberError: false });
        setStaffInfo({
          ...staffInfo,
          phoneNumber: phoneNumber,
        })
      }
    }
  }, [phoneNumberValidity]);

  const { ref } = usePlacesWidget({
    apiKey: process.env.REACT_APP_GOOGLE_APIKEY,
    onPlaceSelected: (place) => {
      const components = place.address_components || [];

      const getComponent = (type) =>
        components.find((component) => component.types.includes(type))?.long_name || "";

      const streetNumber = getComponent("street_number");
      const route = getComponent("route");

      const streetAddress = [streetNumber, route].filter(Boolean).join(" ");
      const streetAddressLine2 = ""; // Google Autocomplete doesn't return this
      const city = getComponent("locality") || getComponent("sublocality") || "";
      const province = getComponent("administrative_area_level_1");
      const postalCode = getComponent("postal_code");
      const country = getComponent("country");
      setStaffInfo({
        ...staffInfo,
        streetAddress,
        streetAddressLine2,
        city,
        province,
        postalCode,
        country
      })
    },
    options: {
      types: ["address"],
    },
  });

  const [createStaff, { loading }] = useMutation(CREATE_STAFF, {
    onCompleted: (data) => {
      if (data.createStaff.id) {
        setStaffs((staffs) => [...staffs, data.createStaff]);
        setSuccessfullyIsOpen(true);
        setFailedIsOpen(false);
        setIsOpen(false);
      } else {
        setFailedIsOpen(true);
        setIsOpen(false);
      }
    },
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message
        if(error.message.includes('Validation')) {
            content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception)
        }
        debouncedErrorMessage(content)
      }
    },
  });

  const handleContinue = () => {
    if(loading) return
    createStaff({
      variables: {
        input: {
          fullName: staffInfo.fullName,
          email: staffInfo.email,
          phoneNumber: staffInfo.phoneNumber,
          address: staffInfo.streetAddress,
          addressLine2: staffInfo.streetAddressLine2,
          city: staffInfo.city,
          province: staffInfo.province,
          postalCode: staffInfo.postalCode,
          country: staffInfo.country,
          userRoleIds: [roleId],
          status: isDeactivated ? "INACTIVE" : "ACTIVE",
        },
      },
    });
  };

  return (
    <>
      <MainModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        hidden={true}
        className="modal-success"
        additionalstyle={`
        max-width: 500px;
        .modal-content{
          height: 100%;
          .close {
            z-index: 200;
          }   
          .modal-body{
            height: 100%;
            padding: 24px;
          }
        }
        background: ${colors.black5};
        top: 179px;
      `}
      >
        <Container>
          <Head>Staff Details</Head>
          <Row>
            <Input
              mainPage
              title="NAME"
              value={staffInfo.fullName}
              error={staffInfoError.fullNameError}
              onChange={(e) => {
                setStaffInfo({ ...staffInfo, fullName: e.target.value });
                setStaffInfoError({ ...staffInfoError, nameError: false });
              }}
              additionalstyle={`
              width: 50%;
            `}
            />
            <Input
              mainPage
              title="ROLE"
              value={role}
              readOnly
              additionalstyle={`
              width: 50%;
            `}
            />
          </Row>
          <Row>
            <Input
              mainPage
              inputFieldRef={ref}
              title="ADDRESS"
              value={staffInfo.streetAddress || ""}
              error={staffInfoError.streetAddressError}
              onChange={(e) => {
                setStaffInfo({ ...staffInfo, streetAddress: e.target.value });
                setStaffInfoError({ ...staffInfoError, streetAddressError: false });
              }}
              additionalstyle={`
              width: 100%;
            `}
            />
          </Row>
          <Row>
            <Input
              mainPage
              title="EMAIL"
              value={email}
              error={staffInfoError.emailError}
              onChange={(e) => {
                setStaffInfo({ ...staffInfo, email: e.target.value });
                setStaffInfoError({ ...staffInfoError, emailError: false });
              }}
              additionalstyle={`
              width: 50%;
            `}
            />
            <PhoneNumberInput
              mainPage
              title="PHONE"
              initialValue={phoneNumber}
              onChangeNumber={setPhoneNumber}
              setPhoneNumberValidity={setPhoneNumberValidity}
              onBlur={() => {
                if (!phoneNumberValidity && phoneNumber.length > 0) {
                  setStaffInfoError({
                    ...staffInfoError,
                    phoneNumberError: true,
                  });
                } else {
                  setStaffInfoError({
                    ...staffInfoError,
                    phoneNumberError: false,
                  })
                }
              }}
              additionalstyle={`
              width: 50%;
              @media only screen and (max-width: 767px) {
                width: 100%;
              }
            `}
            />
          </Row>
          <Checkbox
            checked={isDeactivated}
            onChange={() => setIsDeactivated(!isDeactivated)}
            title="Deactivate Account"
            additionalstyle={`
              margin: 24px 0 0 2px;
            `}
          />
        </Container>
        <Footer>
          <div className="buttonContainer">
            <Button
              danger
              additionalstyle={`
              width: 79px;
              margin-right: 16px;
            `}
              onClick={() => setIsOpen(false)}
            >
              Cancel
            </Button>
            <Button
              success
              additionalstyle={`
              width: 97px;
            `}
              onClick={handleContinue}
              data-loading={loading}
            >
              {loading? null: 'Continue'}
            </Button>
          </div>
        </Footer>
      </MainModal>
      <ModalGeneralSuccessfully
        isOpen={successfullyIsOpen}
        setIsOpen={setSuccessfullyIsOpen}
        role={role}
        fullName={staffInfo.fullName}
        type="createStaff"
      />
      <ModalGeneralFailed
        isOpen={failedIsOpen}
        setIsOpen={setFailedIsOpen}
        retryFunction={handleContinue}
        type="createStaff"
      />
    </>
  );
};

export default ModalStaffInformation;
